import { cx } from "@emotion/css";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { TableBody } from "@mui/material";
import MuiTable from "@mui/material/Table";
import MUITableCell from "@mui/material/TableCell";
import TableFooter from "@mui/material/TableFooter";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import MUITableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import type { ComponentType, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox } from "src/components/Default/LekUI/Checkbox";
import Menu from "src/components/Default/LekUI/Menu";
import { Tooltip } from "src/components/Default/LekUI/Tooltip";
import isMobile from "src/helpers/isMobile";

import MobileTable from "./MobileTable";

interface TableCellProps {
	children?: ReactNode;
	className?: string;
	colSpan?: number;
	align?: "left" | "center" | "right";
	sortDirection?: "asc" | "desc" | false;
}

const TableCell = ({ children, className, ...props }: TableCellProps) => {
	return (
		<MUITableCell {...props} className={cx(className, "cell")}>
			{children}
		</MUITableCell>
	);
};

interface TableRowProps {
	children: ReactNode;
	className?: string;
	[key: string]: unknown;
}

const TableRow = ({ children, className, ...rest }: TableRowProps) => {
	return (
		<MUITableRow className={cx(className, "row")} {...rest}>
			{children}
		</MUITableRow>
	);
};

interface StyledTableCellProps extends TableCellProps {
	title?: string;
}

export function StyledTableCell({ title, ...props }: StyledTableCellProps) {
	if (!title) {
		return <TableCell {...props}>{props.children}</TableCell>;
	}

	return (
		<TableCell {...props}>
			<Tooltip title={title} wrapperClassName="text-ellipsis-1">
				{props.children}
			</Tooltip>
		</TableCell>
	);
}

type SortOrder = "ASC" | "DESC";

interface TableHeader {
	key?: string;
	value?: string;
	getSortKey?: (row: TableRowData) => number;
}

interface TableRowData {
	id: string | number;
	[key: string]: unknown;
}

interface SelectedElement {
	id: string | number;
	[key: string]: unknown;
}

interface DefaultTableProps {
	rows: TableRowData[];
	take: number;
	page: number;
	orderBy: string;
	order: SortOrder;
	setDetails: (
		details: Partial<{
			take: number;
			page: number;
			orderBy: string;
			order: SortOrder;
		}>,
	) => void;
	RowActions?: ComponentType<{ row: TableRowData }>;
	EmptyBox?: ReactNode;
	headers?: TableHeader[];
	renderRow?: (row: TableRowData) => ReactNode;
	selectedElements?: SelectedElement[];
	onRowSelected?: (row: TableRowData) => void;
	getOnSelectActions?: () => Array<{
		title: string;
		onClick: (event: React.MouseEvent) => void;
	}>;
	count: number;
	renderMobileRow?: (
		row: TableRowData & { disabled: boolean },
		className: string,
	) => ReactNode;
	getRowProps?: (row: TableRowData) => Record<string, unknown>;
	clearSelectedRows?: () => void;
	searchValue?: string;
}

export default function DefaultTable({
	rows,
	take,
	page,
	orderBy,
	order,
	setDetails,
	RowActions,
	EmptyBox = null,
	...props
}: DefaultTableProps) {
	const { t } = useTranslation();
	const onChangeOrder = (header: string) => {
		if (header === orderBy) {
			setDetails({ order: order === "ASC" ? "DESC" : "ASC" });
		} else {
			setDetails({ orderBy: header });
		}
	};

	const renderMenu = (row: TableRowData) => {
		return (
			<Menu
				className="justify-center"
				buttonClassName="bg-transparent"
				buttonTestId={`row-menu-button-${row.id}`}
				icon={<MoreVertIcon />}
				Actions={<RowActions row={row} />}
				closeAfterClick
			/>
		);
	};

	const renderSortKey = (
		value: string | undefined,
		key: string | undefined,
	) => {
		if (value) {
			return (
				<TableSortLabel
					active={orderBy === value}
					direction={
						orderBy === value ? (order.toLowerCase() as "asc" | "desc") : "asc"
					}
					onClick={() => onChangeOrder(value)}
				>
					{key}
				</TableSortLabel>
			);
		}

		return key;
	};

	const renderHeaders = () => {
		return props.headers.map(({ value, key }, index) => (
			<StyledTableCell
				key={key ?? `header-${index}`}
				className="size-14-light text-center"
				sortDirection={
					orderBy === value ? (order.toLowerCase() as "asc" | "desc") : false
				}
			>
				{renderSortKey(value, key)}
			</StyledTableCell>
		));
	};

	if (isMobile()) {
		return (
			<MobileTable
				{...props}
				list={rows}
				onSelectActions={props.getOnSelectActions}
				page={page}
				setPage={(newPage: number) => setDetails({ page: newPage })}
				selectedList={props.selectedElements}
				addToSelectedList={(row: TableRowData) => props.onRowSelected(row)}
				count={Math.ceil(props.count / take)}
				clearSelectedRows={props.clearSelectedRows}
				EmptyBox={EmptyBox}
			/>
		);
	}

	const renderRows = () => {
		return rows.map((row) => (
			<TableRow key={row.id} {...props.getRowProps?.(row)}>
				<StyledTableCell>
					<Checkbox
						color="primary"
						checked={props.selectedElements.some(
							(element) => element.id === row.id,
						)}
						onChange={() => props.onRowSelected(row)}
					/>
				</StyledTableCell>
				{props.renderRow(row)}
				<StyledTableCell className="text-center size-16-light">
					{renderMenu(row)}
				</StyledTableCell>
			</TableRow>
		));
	};

	const renderEmptyRow = () => {
		if (rows.length) {
			return null;
		}

		return (
			<TableRow key="empty">
				<StyledTableCell colSpan={props.headers.length} align="center">
					{EmptyBox}
				</StyledTableCell>
			</TableRow>
		);
	};

	return (
		<MuiTable aria-label="customized table">
			<TableHead>
				<TableRow className="row-header">{renderHeaders()}</TableRow>
			</TableHead>
			<TableBody>
				{renderRows()}
				{renderEmptyRow()}
			</TableBody>
			<TableFooter>
				<TableRow className="row-footer">
					<TablePagination
						rowsPerPageOptions={[5, 10, 25]}
						count={props.count}
						rowsPerPage={take}
						page={page}
						onPageChange={(_e, newPage) => setDetails({ page: newPage })}
						onRowsPerPageChange={(e) =>
							setDetails({ take: Number(e.target.value) })
						}
						labelRowsPerPage={`${t("table.elementsOnPage")}: `}
						labelDisplayedRows={({ from, to, count }) =>
							`${from}-${to} z ${count}`
						}
					/>
				</TableRow>
			</TableFooter>
		</MuiTable>
	);
}
